import { useState } from 'react'

/**
 * Custom hook for managing loading states
 * Provides start, stop, and async wrapper functionality
 * 
 * @param {boolean} initialState - Initial loading state (default: false)
 * @returns {Object} Loading state and handlers
 */
export function useLoading(initialState = false) {
    const [loading, setLoading] = useState(initialState)

    const startLoading = () => setLoading(true)

    const stopLoading = () => setLoading(false)

    /**
     * Wraps an async function with loading state management
     * Automatically starts loading before execution and stops after completion
     * 
     * @param {Function} asyncFn - Async function to execute
     * @returns {Promise} Result of the async function
     */
    const withLoading = async (asyncFn) => {
        startLoading()
        try {
            const result = await asyncFn()
            return result
        } catch (error) {
            throw error
        } finally {
            stopLoading()
        }
    }

    return {
        loading,
        startLoading,
        stopLoading,
        withLoading,
        setLoading,
    }
}
